import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { FaCheck, FaTimes } from "react-icons/fa";
import Swal from "sweetalert2";
import {
  fetchApplicationFullDetails,
  fetchCommissionInquiryFees,
  fetchCourseIntakes,
  fetchCoursesForProvider,
  fetchIntakes,
  fetchPackagesForProvider,
  updateApplicationStudentCourseDetails,
  upsertCommissionInquiryFees,
} from "./commissionsApi";
import {
  buildBatchItemUpdatePayload,
  buildInquiryUpdatePayload,
  getInquiryFieldsFromApplication,
  getMissingInquiryBatchFields,
  getMissingInquiryFields,
  isInquiryBatchComplete,
  isInquiryComplete,
  resolveInquiryDurationLabel,
  toInquiryInputDate,
} from "./inquiryApplicationUtils";
import { updateInquiryBatchItem } from "./inquiryBatchApi";
import { mapCommissionDetailViewModel } from "./detail/commissionDetailMapper";

function firstDefinedString(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text && text !== "-" && text !== "—") return text;
  }
  return "";
}

function mergeInquirySources(
  application,
  detailPayload,
  courseId,
  applicationCourseId,
) {
  const fromList = getInquiryFieldsFromApplication(application || {});
  if (!detailPayload || typeof detailPayload !== "object") return fromList;

  const viewModel = mapCommissionDetailViewModel(detailPayload, {
    courseId: courseId || fromList.courseId,
    applicationCourseId:
      fromList.applicationCourseId || application?.application_course_id,
  });
  const client = detailPayload.client || detailPayload.student || {};
  const catalogCourse = detailPayload.course || {};
  const provider = detailPayload.provider || {};
  const applicationRow = detailPayload.application || {};
  const cnic = detailPayload.cnic || detailPayload.clientcnic || {};
  const plan = viewModel?.commissionPlan?.existing || {};
  const planDefaults = viewModel?.commissionPlan?.defaults || {};
  const inquiry = viewModel?.inquiry || {};

  const enrichedApp = {
    ...application,
    clientname: fromList.clientName || client.name || application?.clientname,
    student_id:
      fromList.studentId ||
      client.student_id ||
      client.studentId ||
      viewModel?.student?.studentId ||
      application?.student_id,
    dob:
      fromList.dob ||
      cnic.dob ||
      client.dob ||
      viewModel?.student?.birthDate ||
      application?.dob,
    coursename:
      fromList.course ||
      inquiry.course ||
      viewModel?.course?.name ||
      catalogCourse.name ||
      catalogCourse.coursename ||
      application?.coursename,
    providername:
      fromList.provider ||
      provider.name ||
      inquiry.providerName ||
      application?.providername,
    intake:
      fromList.intake ||
      inquiry.intake ||
      applicationRow.intake_name ||
      application?.intake,
    tuition_fee: firstDefinedString(
      fromList.tuitionFee,
      inquiry.tuitionFee,
      viewModel?.course?.tuitionFee,
      plan.tuitionFee,
      plan.tuition_fee,
      planDefaults.tuitionFee,
      catalogCourse.tuition_fee,
      catalogCourse.tuitionFee,
      application?.tuition_fee,
    ),
    commissionable_tuition_fee: firstDefinedString(
      fromList.commissionableTuitionFee,
      inquiry.commissionable_tuition_fee,
      plan.commissionableTuitionFee,
      plan.commissionable_tuition_fee,
      planDefaults.commissionableTuitionFee,
      applicationRow.commissionable_tuition_fee,
      applicationRow.commissionableTuitionFee,
      application?.commissionable_tuition_fee,
      application?.commissionableTuitionFee,
    ),
    application_fee: firstDefinedString(
      fromList.applicationFee,
      application?.application_fee,
      application?.applicationFee,
    ),
    material_fee: firstDefinedString(
      fromList.materialFee,
      application?.material_fee,
      application?.materialFee,
    ),
    currency:
      fromList.currency ||
      plan.currency ||
      planDefaults.currency ||
      application?.currency ||
      "AUD",
    start_date:
      fromList.start ||
      inquiry.start ||
      viewModel?.course?.startDate ||
      catalogCourse.start_date ||
      catalogCourse.startdate ||
      application?.start_date,
    end_date:
      fromList.end ||
      inquiry.end ||
      viewModel?.course?.endDate ||
      catalogCourse.end_date ||
      catalogCourse.enddate ||
      application?.end_date,
    application_course_id:
      fromList.applicationCourseId ||
      inquiry.applicationCourseId ||
      viewModel?.course?.applicationCourseId ||
      plan.applicationCourseId ||
      plan.application_course_id ||
      catalogCourse.id ||
      catalogCourse.application_course_id ||
      application?.application_course_id,
  };

  return getInquiryFieldsFromApplication(enrichedApp);
}

function buildInquiryValuesFromFields(fields = {}) {
  return {
    studentId: fields.studentId || "",
    dob: toInquiryInputDate(fields.dob) || "",
    course: fields.course || "",
    intake: fields.intake || "",
    start: toInquiryInputDate(fields.start) || "",
    end: toInquiryInputDate(fields.end) || "",
    tuitionFee: fields.tuitionFee || "",
    commissionableTuitionFee: fields.commissionableTuitionFee || "",
    applicationFee: fields.applicationFee || "",
    materialFee: fields.materialFee || "",
    courseId: fields.courseId || null,
    currency: fields.currency || "AUD",
  };
}

function applyFeeRecordToFields(fields = {}, feeRecord = null) {
  if (!feeRecord || typeof feeRecord !== "object") return fields;
  return {
    ...fields,
    applicationFee:
      feeRecord.application_fee != null && feeRecord.application_fee !== ""
        ? String(feeRecord.application_fee)
        : fields.applicationFee || "",
    materialFee:
      feeRecord.material_fee != null && feeRecord.material_fee !== ""
        ? String(feeRecord.material_fee)
        : fields.materialFee || "",
    currency: feeRecord.currency || fields.currency || "AUD",
  };
}

const inputClass =
  "h-8 w-full rounded border border-slate-200 bg-white px-2 text-[12px] font-medium text-slate-800 outline-none transition focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 disabled:bg-slate-50";

const selectClass = `${inputClass} appearance-none`;

function resolveProductKeyFromName(courseName = "", courses = [], packages = []) {
  const name = String(courseName || "").trim();
  if (!name) return "";

  const course = courses.find(
    (row) => String(row?.name || "").trim() === name,
  );
  if (course?.id != null) return `course:${course.id}`;

  const pkg = packages.find((row) => String(row?.name || "").trim() === name);
  if (pkg?.id != null) return `package:${pkg.id}`;

  return `custom:${name}`;
}

function resolveIntakeIdFromTitle(intakeTitle = "", intakes = []) {
  const title = String(intakeTitle || "").trim();
  if (!title) return "";

  const match = intakes.find(
    (row) =>
      String(row?.title || row?.intake_title || "").trim() === title,
  );
  return match?.id != null ? String(match.id) : "";
}

function resolveIntakeTitleFromId(intakeId = "", intakes = []) {
  if (!intakeId) return "";
  const match = intakes.find(
    (row) => String(row?.id) === String(intakeId),
  );
  return String(match?.title || match?.intake_title || "").trim();
}

function resolveLinkedIntakeSelection(
  linkedRows = [],
  intakes = [],
  currentIntakeTitle = "",
) {
  if (!linkedRows.length) return null;

  const resolveFromRow = (row) => {
    const intakeId = row?.intake_id ?? row?.intakeid ?? row?.intake?.id;
    if (intakeId != null) {
      const match = intakes.find((item) => String(item.id) === String(intakeId));
      if (match) {
        return {
          intakeId: String(match.id),
          intakeTitle: String(match.title || match.intake_title || "").trim(),
        };
      }
    }
    const title = String(row?.title || row?.intake_title || "").trim();
    const id = resolveIntakeIdFromTitle(title, intakes);
    if (id) {
      return {
        intakeId: id,
        intakeTitle: title || resolveIntakeTitleFromId(id, intakes),
      };
    }
    return null;
  };

  const currentTitle = String(currentIntakeTitle || "").trim();
  if (currentTitle) {
    for (const row of linkedRows) {
      const resolved = resolveFromRow(row);
      if (resolved && resolved.intakeTitle === currentTitle) return resolved;
    }
  }

  if (linkedRows.length === 1) {
    return resolveFromRow(linkedRows[0]);
  }

  const resolvedRows = linkedRows.map(resolveFromRow).filter(Boolean);
  if (resolvedRows.length === 1) return resolvedRows[0];

  return null;
}

function Label({ children, required, error }) {
  return (
    <span className="mb-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
      {children}
      {required ? <span className="text-red-500">*</span> : null}
      {error ? (
        <span className="normal-case tracking-normal text-red-500">· {error}</span>
      ) : null}
    </span>
  );
}

function ReadOnlyChip({ label, value, missing }) {
  return (
    <div className="min-w-0 rounded border border-slate-100 bg-slate-50 px-2 py-1">
      <p className="mb-0 text-[9px] font-semibold uppercase text-slate-400">
        {label}
      </p>
      <p
        className={`mb-0 truncate text-[11px] font-semibold ${
          missing ? "text-red-600" : "text-slate-700"
        }`}
        title={value || undefined}
      >
        {missing ? "---" : value}
      </p>
    </div>
  );
}

function InquiryFeeFields({ values, saving, disabled, onChange }) {
  return (
    <div className="space-y-2 border-t border-slate-100 pt-2.5">
      <p className="mb-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        Fees
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="block min-w-0">
          <Label>Tuition fee</Label>
          <input
            type="number"
            min="0"
            step="0.01"
            className={inputClass}
            value={values.tuitionFee}
            disabled={saving || disabled}
            placeholder="12000"
            onChange={(e) => onChange("tuitionFee", e.target.value)}
          />
        </label>
        <label className="block min-w-0">
          <Label>Commissionable tuition fee</Label>
          <input
            type="number"
            min="0"
            step="0.01"
            className={inputClass}
            value={values.commissionableTuitionFee}
            disabled={saving || disabled}
            placeholder="10000"
            onChange={(e) =>
              onChange("commissionableTuitionFee", e.target.value)
            }
          />
        </label>
        <label className="block min-w-0">
          <Label>Application fee</Label>
          <input
            type="number"
            min="0"
            step="0.01"
            className={inputClass}
            value={values.applicationFee}
            disabled={saving || disabled}
            placeholder="250"
            onChange={(e) => onChange("applicationFee", e.target.value)}
          />
        </label>
        <label className="block min-w-0">
          <Label>Material fee</Label>
          <input
            type="number"
            min="0"
            step="0.01"
            className={inputClass}
            value={values.materialFee}
            disabled={saving || disabled}
            placeholder="500"
            onChange={(e) => onChange("materialFee", e.target.value)}
          />
        </label>
      </div>
    </div>
  );
}

function InquiryFillModal({
  open,
  application,
  batchId,
  mode = "fill",
  validationMode = "full",
  title = "Fill inquiry data",
  onClose,
  onSaved,
  onSavedAndContinue,
}) {
  const base = useMemo(
    () => getInquiryFieldsFromApplication(application || {}),
    [application],
  );

  const [resolvedBase, setResolvedBase] = useState(base);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [values, setValues] = useState({
    studentId: "",
    dob: "",
    course: "",
    intake: "",
    start: "",
    end: "",
    tuitionFee: "",
    commissionableTuitionFee: "",
    applicationFee: "",
    materialFee: "",
    courseId: null,
    currency: "AUD",
  });
  const [courses, setCourses] = useState([]);
  const [packages, setPackages] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [productKey, setProductKey] = useState("");
  const [intakeId, setIntakeId] = useState("");
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !application) return undefined;
    let cancelled = false;

    setResolvedBase(base);
    setValues(buildInquiryValuesFromFields(base));
    setProductKey("");
    setIntakeId("");
    setCourses([]);
    setPackages([]);
    setIntakes([]);
    setSaving(false);

    const shouldFetchDetails = Boolean(base.applicationId);

    const canFetchFees =
      base.applicationId && base.clientId && base.providerId;

    if (!shouldFetchDetails && !canFetchFees) return undefined;

    setLoadingDetails(true);

    const detailPromise = shouldFetchDetails
      ? fetchApplicationFullDetails({
          applicationId: base.applicationId,
          type: base.type,
          clientId: base.clientId,
          providerId: base.providerId,
          courseId: base.courseId,
          applicationCourseId: base.applicationCourseId,
        })
      : Promise.resolve(null);

    const feePromise = canFetchFees
      ? fetchCommissionInquiryFees({
          applicationId: base.applicationId,
          clientId: base.clientId,
          providerId: base.providerId,
          applicationCourseId: base.applicationCourseId,
        }).catch(() => null)
      : Promise.resolve(null);

    Promise.all([detailPromise, feePromise])
      .then(([detail, feeRecord]) => {
        if (cancelled) return;
        let enriched = shouldFetchDetails
          ? mergeInquirySources(
              application,
              detail,
              base.courseId,
              base.applicationCourseId,
            )
          : base;
        enriched = applyFeeRecordToFields(enriched, feeRecord);
        setResolvedBase(enriched);
        setValues(buildInquiryValuesFromFields(enriched));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingDetails(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, application, base, validationMode]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !saving) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, saving]);

  const isBatchMode = validationMode === "batch";
  const isBatchItemEdit = isBatchMode && Boolean(application?._batchItemId);

  useEffect(() => {
    if (!open || !isBatchMode) return undefined;

    const providerId = resolvedBase.providerId;
    let cancelled = false;
    setLoadingCatalog(true);

    Promise.all([
      providerId ? fetchCoursesForProvider(providerId) : Promise.resolve([]),
      providerId ? fetchPackagesForProvider(providerId) : Promise.resolve([]),
      fetchIntakes(),
    ])
      .then(([coursesData, packagesData, intakesData]) => {
        if (cancelled) return;
        setCourses(coursesData);
        setPackages(packagesData);
        setIntakes(intakesData);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingCatalog(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isBatchMode, resolvedBase.providerId]);

  useEffect(() => {
    if (!open || !isBatchMode) return;
    if (loadingCatalog) return;

    const nextProductKey =
      resolveProductKeyFromName(values.course, courses, packages) ||
      (values.course ? `custom:${values.course}` : "");
    setProductKey(nextProductKey);
    const id = resolveIntakeIdFromTitle(values.intake, intakes);
    setIntakeId(id || (values.intake ? `custom:${values.intake}` : ""));
  }, [
    open,
    isBatchMode,
    loadingCatalog,
    values.course,
    values.intake,
    courses,
    packages,
    intakes,
  ]);

  const sortedCourses = useMemo(
    () =>
      [...courses].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
      ),
    [courses],
  );

  const sortedPackages = useMemo(
    () =>
      [...packages].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
      ),
    [packages],
  );

  const sortedIntakes = useMemo(
    () =>
      [...intakes].sort((a, b) =>
        String(a?.title || a?.intake_title || "").localeCompare(
          String(b?.title || b?.intake_title || ""),
        ),
      ),
    [intakes],
  );

  const catalogBusy = loadingDetails || loadingCatalog;

  const dateRangeInvalid = useMemo(() => {
    if (!values.start || !values.end) return false;
    return values.start > values.end;
  }, [values.start, values.end]);

  const merged = useMemo(
    () => ({
      ...resolvedBase,
      ...values,
      duration: dateRangeInvalid
        ? ""
        : resolveInquiryDurationLabel(values.start, values.end),
    }),
    [resolvedBase, values, dateRangeInvalid],
  );

  const missing = useMemo(
    () =>
      validationMode === "batch"
        ? getMissingInquiryBatchFields(merged)
        : getMissingInquiryFields(merged),
    [merged, validationMode],
  );
  const missingKeys = useMemo(
    () => new Set(missing.map((item) => item.key)),
    [missing],
  );
  const complete =
    !dateRangeInvalid &&
    (isBatchMode
      ? missing.length === 0
      : missing.length === 0 && isInquiryComplete(merged));

  const identityMissing = missingKeys.has("studentIdOrDob");
  const studentIdError = isBatchMode
    ? identityMissing && !String(values.studentId || "").trim()
    : missingKeys.has("studentId");
  const dobError = isBatchMode
    ? identityMissing && !toInquiryInputDate(values.dob)
    : missingKeys.has("dob");

  const updateField = (key, value) => {
    setValues((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "start" && next.end && value && value > next.end) {
        next.end = "";
      }
      if (key === "end" && next.start && value && value < next.start) {
        return prev;
      }
      return next;
    });
  };

  const applyIntakeSelection = (nextIntakeId) => {
    if (String(nextIntakeId || "").startsWith("custom:")) {
      const title = String(nextIntakeId).slice("custom:".length);
      setIntakeId(nextIntakeId);
      setValues((prev) => ({ ...prev, intake: title }));
      return;
    }

    setIntakeId(nextIntakeId);
    setValues((prev) => ({
      ...prev,
      intake: resolveIntakeTitleFromId(nextIntakeId, intakes),
    }));
  };

  const handleProductChange = async (event) => {
    const key = event.target.value;
    setProductKey(key);

    if (!key) {
      setValues((prev) => ({ ...prev, course: "", courseId: null }));
      return;
    }

    if (key.startsWith("course:")) {
      const id = key.slice("course:".length);
      const course = courses.find((row) => String(row.id) === String(id));
      const name = String(course?.name || "").trim();
      setValues((prev) => ({
        ...prev,
        course: name,
        courseId: id,
      }));

      const providerId = resolvedBase.providerId;
      if (providerId && id) {
        const linked = await fetchCourseIntakes(id, providerId);
        const resolved = resolveLinkedIntakeSelection(
          linked,
          intakes,
          values.intake,
        );
        if (resolved?.intakeId) {
          applyIntakeSelection(resolved.intakeId);
        }
      }
      return;
    }

    if (key.startsWith("package:")) {
      const id = key.slice("package:".length);
      const pkg = packages.find((row) => String(row.id) === String(id));
      const name = String(pkg?.name || "").trim();
      setValues((prev) => ({
        ...prev,
        course: name,
        courseId: null,
      }));
      return;
    }

    if (key.startsWith("custom:")) {
      const name = key.slice("custom:".length);
      setValues((prev) => ({
        ...prev,
        course: name,
        courseId: null,
      }));
    }
  };

  const handleIntakeChange = (event) => {
    applyIntakeSelection(event.target.value);
  };

  const handleSave = async ({ continueToQueue = false } = {}) => {
    if (saving) return;
    if (dateRangeInvalid) {
      Swal.fire(
        "Invalid dates",
        "Course start date cannot be after the end date.",
        "warning",
      );
      return;
    }
    if (isBatchMode) {
      if (missing.length > 0) {
        Swal.fire(
          "Missing inquiry data",
          `Please fill: ${missing.map((m) => m.label).join(", ")}`,
          "warning",
        );
        return;
      }
    } else if (!isInquiryComplete(merged)) {
      Swal.fire(
        "Missing inquiry data",
        `Please fill: ${missing.map((m) => m.label).join(", ")}`,
        "warning",
      );
      return;
    }

    setSaving(true);
    try {
      if (isBatchItemEdit && batchId) {
        await updateInquiryBatchItem(
          batchId,
          application._batchItemId,
          buildBatchItemUpdatePayload(values),
        );
      } else {
        const saveValues = isBatchMode
          ? {
              ...values,
              tuitionFee: values.tuitionFee || resolvedBase.tuitionFee || "",
              commissionableTuitionFee:
                values.commissionableTuitionFee ||
                resolvedBase.commissionableTuitionFee ||
                "",
            }
          : values;

        const tasks = [
          updateApplicationStudentCourseDetails(
            buildInquiryUpdatePayload(application, saveValues, {
              applicationCourseId: resolvedBase.applicationCourseId,
              applicationId: resolvedBase.applicationId,
              clientId: resolvedBase.clientId,
              providerId: resolvedBase.providerId,
              courseId: saveValues.courseId || resolvedBase.courseId,
              intakeId:
                intakeId && !String(intakeId).startsWith("custom:")
                  ? intakeId
                  : undefined,
              type: resolvedBase.type,
            }),
          ),
        ];

        if (resolvedBase.applicationId && resolvedBase.clientId && resolvedBase.providerId) {
          tasks.push(
            upsertCommissionInquiryFees({
              application_id: resolvedBase.applicationId,
              client_id: resolvedBase.clientId,
              provider_id: resolvedBase.providerId,
              application_course_id: resolvedBase.applicationCourseId,
              application_fee: saveValues.applicationFee,
              material_fee: saveValues.materialFee,
              currency: saveValues.currency || resolvedBase.currency || "AUD",
            }),
          );
        }

        await Promise.all(tasks);
      }
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: continueToQueue
          ? "Inquiry details saved. Adding to inquiry…"
          : "Inquiry details updated.",
        timer: continueToQueue ? 900 : 1200,
        showConfirmButton: false,
      });
      if (continueToQueue && typeof onSavedAndContinue === "function") {
        await onSavedAndContinue(application);
      } else if (typeof onSaved === "function") {
        onSaved(application);
      }
      onClose?.();
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to save inquiry details.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open || !application) return null;

  const clientPath = resolvedBase.clientId
    ? `/clients/view-client-detail/${resolvedBase.clientId}`
    : null;

  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop bg-black/40"
        onClick={saving ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered flex items-center justify-center p-2 sm:p-3"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inquiry-fill-modal-title"
        onClick={saving ? undefined : onClose}
      >
        <div
          className="tailwind-container relative z-10 flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <div className="min-w-0 flex-1">
              <h3
                id="inquiry-fill-modal-title"
                className="mb-0 text-[13px] font-semibold text-slate-900"
              >
                {title}
              </h3>
              <p className="mb-0 text-[10px] text-slate-400">
                {loadingDetails
                  ? "Loading application details…"
                  : loadingCatalog
                    ? "Loading courses and intakes…"
                    : isBatchMode
                    ? missing.length
                      ? `${missing.length} field${missing.length === 1 ? "" : "s"} still missing for inquiry`
                      : "Update course, intake, student ID, DOB, dates, or fees"
                    : missing.length
                      ? `${missing.length} required empty`
                      : dateRangeInvalid
                        ? "Fix start / end dates"
                        : "Ready to submit"}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Close"
            >
              <FaTimes size={11} />
            </button>
          </div>

          <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-3 py-2.5">
            <div className="flex flex-wrap items-start justify-between gap-1.5">
              <div className="min-w-0">
                <p className="mb-0 text-[12px] font-semibold text-slate-800">
                  {merged.clientName || "---"}
                </p>
                {isBatchMode && merged.provider ? (
                  <p className="mb-0 mt-0.5 text-[11px] font-medium text-slate-500">
                    {merged.provider}
                  </p>
                ) : null}
              </div>
              {clientPath ? (
                <Link
                  to={clientPath}
                  className="text-[11px] font-semibold text-[#007BFF] hover:underline"
                >
                  Open client
                </Link>
              ) : null}
            </div>

            {isBatchMode ? (
              <>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <label className="block min-w-0">
                    <Label required error={missingKeys.has("course") ? "required" : ""}>
                      Course / package
                    </Label>
                    <select
                      className={`${selectClass} ${
                        missingKeys.has("course") ? "border-red-300" : ""
                      }`}
                      value={productKey}
                      disabled={saving || catalogBusy}
                      onChange={handleProductChange}
                    >
                      <option value="">
                        {catalogBusy ? "Loading…" : "Select course or package"}
                      </option>
                      {productKey.startsWith("custom:") ? (
                        <option value={productKey}>{values.course} (current)</option>
                      ) : null}
                      {sortedCourses.length > 0 ? (
                        <optgroup label="Courses">
                          {sortedCourses.map((course) => (
                            <option
                              key={`course-${course.id}`}
                              value={`course:${course.id}`}
                            >
                              {course.name}
                            </option>
                          ))}
                        </optgroup>
                      ) : null}
                      {sortedPackages.length > 0 ? (
                        <optgroup label="Packages">
                          {sortedPackages.map((pkg) => (
                            <option
                              key={`package-${pkg.id}`}
                              value={`package:${pkg.id}`}
                            >
                              {pkg.name}
                            </option>
                          ))}
                        </optgroup>
                      ) : null}
                    </select>
                  </label>
                  <label className="block min-w-0">
                    <Label required error={missingKeys.has("intake") ? "required" : ""}>
                      Intake
                    </Label>
                    <select
                      className={`${selectClass} ${
                        missingKeys.has("intake") ? "border-red-300" : ""
                      }`}
                      value={intakeId}
                      disabled={saving || catalogBusy}
                      onChange={handleIntakeChange}
                    >
                      <option value="">
                        {catalogBusy ? "Loading…" : "Select intake"}
                      </option>
                      {intakeId.startsWith("custom:") ? (
                        <option value={intakeId}>{values.intake} (current)</option>
                      ) : null}
                      {sortedIntakes.map((intake) => (
                        <option key={intake.id} value={String(intake.id)}>
                          {intake.title || intake.intake_title}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <label className="block min-w-0">
                    <Label error={studentIdError ? "required" : ""}>
                      Student ID
                      <span className="ml-1 normal-case font-normal text-slate-400">
                        (or DOB)
                      </span>
                    </Label>
                    <input
                      type="text"
                      className={`${inputClass} ${
                        studentIdError ? "border-red-300" : ""
                      }`}
                      value={values.studentId}
                      disabled={saving || catalogBusy}
                      placeholder="PS-009"
                      onChange={(e) => updateField("studentId", e.target.value)}
                    />
                  </label>
                  <label className="block min-w-0">
                    <Label error={dobError ? "required" : ""}>
                      DOB
                      <span className="ml-1 normal-case font-normal text-slate-400">
                        (or Student ID)
                      </span>
                    </Label>
                    <input
                      type="date"
                      className={`${inputClass} ${dobError ? "border-red-300" : ""}`}
                      value={values.dob}
                      disabled={saving || catalogBusy}
                      onChange={(e) => updateField("dob", e.target.value)}
                    />
                  </label>
                  <label className="block min-w-0">
                    <Label
                      required
                      error={
                        missingKeys.has("start")
                          ? "required"
                          : dateRangeInvalid
                            ? "after end"
                            : ""
                      }
                    >
                      Course start date
                    </Label>
                    <input
                      type="date"
                      max={values.end || undefined}
                      className={`${inputClass} ${
                        missingKeys.has("start") || dateRangeInvalid
                          ? "border-red-300"
                          : ""
                      }`}
                      value={values.start}
                      disabled={saving || catalogBusy}
                      onChange={(e) => updateField("start", e.target.value)}
                    />
                  </label>
                  <label className="block min-w-0">
                    <Label
                      required
                      error={
                        missingKeys.has("end")
                          ? "required"
                          : dateRangeInvalid
                            ? "before start"
                            : ""
                      }
                    >
                      Course end date
                    </Label>
                    <input
                      type="date"
                      min={values.start || undefined}
                      className={`${inputClass} ${
                        missingKeys.has("end") || dateRangeInvalid
                          ? "border-red-300"
                          : ""
                      }`}
                      value={values.end}
                      disabled={saving || catalogBusy}
                      onChange={(e) => updateField("end", e.target.value)}
                    />
                  </label>
                </div>

                <InquiryFeeFields
                  values={values}
                  saving={saving}
                  disabled={catalogBusy}
                  onChange={updateField}
                />
              </>
            ) : (
              <>
            <div className="grid grid-cols-3 gap-1.5">
              <ReadOnlyChip
                label="Course"
                value={merged.course}
                missing={missingKeys.has("course")}
              />
              <ReadOnlyChip
                label="Provider"
                value={merged.provider}
                missing={missingKeys.has("provider")}
              />
              <ReadOnlyChip
                label="Intake"
                value={merged.intake}
                missing={missingKeys.has("intake")}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <label className="block min-w-0">
                <Label
                  required
                  error={studentIdError ? "empty" : ""}
                >
                  Student ID
                </Label>
                <input
                  type="text"
                  className={`${inputClass} ${
                    studentIdError ? "border-red-300" : ""
                  }`}
                  value={values.studentId}
                  disabled={saving}
                  placeholder="PS-009"
                  onChange={(e) => updateField("studentId", e.target.value)}
                />
              </label>
              <label className="block min-w-0">
                <Label required error={dobError ? "empty" : ""}>
                  DOB
                </Label>
                <input
                  type="date"
                  className={`${inputClass} ${dobError ? "border-red-300" : ""}`}
                  value={values.dob}
                  disabled={saving}
                  onChange={(e) => updateField("dob", e.target.value)}
                />
              </label>
              <label className="block min-w-0">
                <Label
                  required
                  error={missingKeys.has("tuitionFee") ? "empty" : ""}
                >
                  Tuition
                </Label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={`${inputClass} ${
                    missingKeys.has("tuitionFee") ? "border-red-300" : ""
                  }`}
                  value={values.tuitionFee}
                  disabled={saving}
                  placeholder="12000"
                  onChange={(e) => updateField("tuitionFee", e.target.value)}
                />
              </label>
              <label className="block min-w-0">
                <Label>Commissionable tuition fee</Label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={inputClass}
                  value={values.commissionableTuitionFee}
                  disabled={saving}
                  placeholder="10000"
                  onChange={(e) =>
                    updateField("commissionableTuitionFee", e.target.value)
                  }
                />
              </label>
              <label className="block min-w-0">
                <Label>Application fee</Label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={inputClass}
                  value={values.applicationFee}
                  disabled={saving}
                  placeholder="250"
                  onChange={(e) => updateField("applicationFee", e.target.value)}
                />
              </label>
              <label className="block min-w-0">
                <Label>Material fee</Label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={inputClass}
                  value={values.materialFee}
                  disabled={saving}
                  placeholder="500"
                  onChange={(e) => updateField("materialFee", e.target.value)}
                />
              </label>
              <div className="rounded border border-slate-100 bg-slate-50 px-2 py-1">
                <p className="mb-0 text-[9px] font-semibold uppercase text-slate-400">
                  Duration
                </p>
                <p className="mb-0 text-[12px] font-semibold text-slate-700">
                  {merged.duration || "—"}
                </p>
              </div>
              <label className="block min-w-0">
                <Label
                  required
                  error={
                    missingKeys.has("start")
                      ? "empty"
                      : dateRangeInvalid
                        ? "after end"
                        : ""
                  }
                >
                  Start
                </Label>
                <input
                  type="date"
                  max={values.end || undefined}
                  className={`${inputClass} ${
                    missingKeys.has("start") || dateRangeInvalid
                      ? "border-red-300"
                      : ""
                  }`}
                  value={values.start}
                  disabled={saving}
                  onChange={(e) => updateField("start", e.target.value)}
                />
              </label>
              <label className="block min-w-0">
                <Label
                  required
                  error={
                    missingKeys.has("end")
                      ? "empty"
                      : dateRangeInvalid
                        ? "before start"
                        : ""
                  }
                >
                  End
                </Label>
                <input
                  type="date"
                  min={values.start || undefined}
                  className={`${inputClass} ${
                    missingKeys.has("end") || dateRangeInvalid
                      ? "border-red-300"
                      : ""
                  }`}
                  value={values.end}
                  disabled={saving}
                  onChange={(e) => updateField("end", e.target.value)}
                />
              </label>
            </div>
              </>
            )}

            {dateRangeInvalid ? (
              <p className="mb-0 text-[11px] font-medium text-red-600">
                Start date cannot be after end date.
              </p>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-1.5 border-t border-slate-100 px-3 py-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex h-8 items-center rounded border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleSave({ continueToQueue: false })}
              disabled={saving || !complete}
              className={`inline-flex h-8 items-center gap-1 rounded px-2.5 text-[11px] font-semibold text-white ${
                saving || !complete
                  ? "cursor-not-allowed bg-[#007BFF]/40"
                  : "bg-[#007BFF] hover:bg-[#0069D9]"
              }`}
              style={{ border: "none" }}
            >
              <FaCheck size={10} />
              {saving ? "Saving…" : isBatchMode ? "Save" : "Submit"}
            </button>
            {mode === "fill-and-queue" ? (
              <button
                type="button"
                onClick={() => handleSave({ continueToQueue: true })}
                disabled={saving || !complete}
                className={`inline-flex h-8 items-center gap-1 rounded px-2.5 text-[11px] font-semibold text-white ${
                  saving || !complete
                    ? "cursor-not-allowed bg-violet-400/50"
                    : "bg-violet-600 hover:bg-violet-700"
                }`}
                style={{ border: "none" }}
              >
                <FaCheck size={10} />
                Submit & add
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default InquiryFillModal;
